// src/pages/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";

function Login() {
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user was redirected here because they need admin access
  const adminRequired = location.state?.adminRequired;
  const from = location.state?.from?.pathname || "/admin-dashboard";

  useEffect(() => {
    // If already logged in as admin and came from a protected route, redirect back
    if (isAdmin && location.state?.from) {
      navigate(from, { replace: true });
    }
    // Show admin login if redirected from protected route
    if (adminRequired) {
      setShowAdminLogin(true);
    }
  }, [isAdmin, navigate, from, adminRequired]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Call the login function from AuthContext (which calls the backend API)
      const result = await login(adminUsername, adminPassword);

      if (result.success) {
        // Successful login
        navigate(from, { replace: true });
      } else {
        setError(result.error || "Invalid credentials. Please try again.");
        setAdminPassword("");
      }
    } catch (err) {
      setError("An error occurred during login. Please try again.");
      setAdminPassword("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container py-5 text-center">
        <h1 className="mb-3">Login to WebVenly</h1>
        <p className="lead mb-4">
          WebVenly gives you secure access to your personalized dashboard,
          tools, and documents — no matter what role you play in the service
          delivery process.
        </p>

        {/* Show alert if redirected from protected page */}
        {adminRequired && (
          <div className="row justify-content-center mb-3">
            <div className="col-md-8">
              <div className="alert alert-warning" role="alert">
                <i className="bi bi-lock-fill me-2"></i>
                Admin authentication required to access that page
              </div>
            </div>
          </div>
        )}

        {/* Existing Login Options */}
        <div className="row justify-content-center mb-5">
          <div className="col-md-5 mb-4">
            <div className="card h-100 shadow-sm border-primary">
              <div className="card-body">
                <h4 className="text-primary">Training Environment (TRN)</h4>
                <p>
                  Use the TRN system to practice workflows, train new staff, or
                  preview upcoming changes. This is a safe, sandboxed version of
                  the platform.
                </p>
                <ul className="list-unstyled text-start fs-6">
                  <li>✅ Create test consumers, authorizations, and logs</li>
                  <li>✅ Safely explore new features</li>
                  <li>✅ Practice without affecting real data</li>
                </ul>
                <a
                  href="https://trn.webvenly.com"
                  className="btn btn-outline-primary mt-3"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to TRN
                  <i className="bi bi-box-arrow-up-right ms-2"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="col-md-5 mb-4">
            <div className="card h-100 shadow-sm border-success">
              <div className="card-body">
                <h4 className="text-success">Production Environment (PRD)</h4>
                <p>
                  The live system where official job logs, mileage, reports, and
                  documents are entered and submitted. Please ensure you are
                  authorized before accessing.
                </p>
                <ul className="list-unstyled text-start fs-6">
                  <li>🔒 Secure access required</li>
                  <li>✅ Submit real reports, invoices, and service logs</li>
                  <li>📁 View and manage live consumer data</li>
                </ul>
                <a
                  href="https://prd.webvenly.com"
                  className="btn btn-outline-success mt-3"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to PRD
                  <i className="bi bi-box-arrow-up-right ms-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Login Section */}
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div
              className={`card shadow-sm border-danger ${
                showAdminLogin ? "border-2" : ""
              }`}
            >
              <div className="card-body">
                {!showAdminLogin ? (
                  <>
                    <h5 className="text-danger mb-3">
                      <i className="bi bi-shield-lock-fill me-2"></i>
                      Page Administrator Access
                    </h5>
                    <p className="text-muted">
                      Restricted area for WebVenly site administrators only.
                      Access email marketing tools, analytics, and system
                      settings.
                    </p>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => setShowAdminLogin(true)}
                    >
                      <i className="bi bi-person-fill-gear me-2"></i>
                      Admin Login
                    </button>
                  </>
                ) : (
                  <>
                    <h5 className="text-danger mb-3">
                      <i className="bi bi-shield-lock-fill me-2"></i>
                      Administrator Authentication
                    </h5>
                    {isAdmin ? (
                      <div>
                        <div className="alert alert-success" role="alert">
                          <i className="bi bi-check-circle-fill me-2"></i>
                          You are already logged in as admin
                        </div>
                        <button
                          className="btn btn-success"
                          onClick={() => navigate("/admin-dashboard")}
                        >
                          Go to Admin Dashboard
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleAdminLogin}>
                        <div className="mb-3">
                          <label
                            htmlFor="adminUsername"
                            className="form-label text-start d-block"
                          >
                            Username or Email
                          </label>
                          <input
                            type="text"
                            id="adminUsername"
                            className={`form-control ${
                              error ? "is-invalid" : ""
                            }`}
                            placeholder="Enter username or email"
                            value={adminUsername}
                            onChange={(e) => setAdminUsername(e.target.value)}
                            autoFocus
                            disabled={loading}
                            required
                          />
                        </div>
                        <div className="mb-3">
                          <label
                            htmlFor="adminPassword"
                            className="form-label text-start d-block"
                          >
                            Password
                          </label>
                          <input
                            type="password"
                            id="adminPassword"
                            className={`form-control ${
                              error ? "is-invalid" : ""
                            }`}
                            placeholder="Enter password"
                            value={adminPassword}
                            onChange={(e) => setAdminPassword(e.target.value)}
                            disabled={loading}
                            required
                          />
                        </div>
                        {error && (
                          <div className="alert alert-danger py-2" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {error}
                          </div>
                        )}
                        <div className="d-flex gap-2">
                          <button
                            type="submit"
                            className="btn btn-danger flex-fill"
                            disabled={
                              loading || !adminUsername || !adminPassword
                            }
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Authenticating...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-key-fill me-2"></i>
                                Login as Admin
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              setShowAdminLogin(false);
                              setAdminUsername("");
                              setAdminPassword("");
                              setError("");
                            }}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                    <div className="mt-3">
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        Contact the site owner if you need admin access
                      </small>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Optional Info */}
        <div className="text-muted small mt-4">
          <p>
            If you're unsure which environment to use or having trouble logging
            in, please contact your CRP administrator or email{" "}
            <a href="mailto:support@webvenly.com">support@webvenly.com</a>.
          </p>
        </div>
      </div>
    </Layout>
  );
}

export default Login;
